import { ValidationError } from '../../shared/errors/BaseErrors';

export type ChannelRuleKey = 'advert_suffix' | 'advert_repeat_interval';

export const CHANNEL_RULE_KEYS: ChannelRuleKey[] = ['advert_suffix', 'advert_repeat_interval'];

export const CHANNEL_RULE_SUFFIX_MAX_LENGTH = 300;
export const CHANNEL_RULE_REPEAT_MIN_DAYS = 1;
export const CHANNEL_RULE_REPEAT_MAX_DAYS = 7;

export interface ChannelRuleEntity {
  key: ChannelRuleKey;
  text?: string;
  repeatIntervalDays?: number;
}

const normalizeSuffix = (rule: ChannelRuleEntity): ChannelRuleEntity => {
  const text = (rule.text ?? '').trim();
  if (!text || Array.from(text).length > CHANNEL_RULE_SUFFIX_MAX_LENGTH) throw new ValidationError('channel-rule-suffix-invalid');
  return { key: 'advert_suffix', text };
};

const normalizeRepeatInterval = (rule: ChannelRuleEntity): ChannelRuleEntity => {
  const days = rule.repeatIntervalDays;
  if (days === undefined || !Number.isInteger(days) || days < CHANNEL_RULE_REPEAT_MIN_DAYS || days > CHANNEL_RULE_REPEAT_MAX_DAYS) {
    throw new ValidationError('channel-rule-repeat-interval-invalid');
  }
  return { key: 'advert_repeat_interval', repeatIntervalDays: days };
};

const NORMALIZERS: Record<ChannelRuleKey, (rule: ChannelRuleEntity) => ChannelRuleEntity> = {
  advert_suffix: normalizeSuffix,
  advert_repeat_interval: normalizeRepeatInterval,
};

export const isChannelRuleKey = (key: string): key is ChannelRuleKey => (CHANNEL_RULE_KEYS as string[]).includes(key);

export const normalizeChannelRules = (rules: ChannelRuleEntity[]): ChannelRuleEntity[] => {
  const seen = new Set<ChannelRuleKey>();
  return (rules ?? []).map((rule) => {
    if (!rule || !isChannelRuleKey(rule.key)) throw new ValidationError('channel-rule-unknown');
    if (seen.has(rule.key)) throw new ValidationError('channel-rule-duplicated');
    seen.add(rule.key);
    return NORMALIZERS[rule.key](rule);
  });
};

export const nextAvailableChannelRuleKey = (rules: Pick<ChannelRuleEntity, 'key'>[]): ChannelRuleKey | undefined => {
  const used = new Set((rules ?? []).map((rule) => rule?.key));
  return CHANNEL_RULE_KEYS.find((key) => !used.has(key));
};

export const defaultChannelRule = (key: ChannelRuleKey): ChannelRuleEntity =>
  key === 'advert_repeat_interval' ? { key, repeatIntervalDays: CHANNEL_RULE_REPEAT_MAX_DAYS } : { key, text: '' };

const ruleSignature = (rule: ChannelRuleEntity): string =>
  rule.key === 'advert_suffix'
    ? `${rule.key}:${(rule.text ?? '').trim()}`
    : `${rule.key}:${rule.repeatIntervalDays ?? ''}`;

export const channelRulesEqual = (left: ChannelRuleEntity[] = [], right: ChannelRuleEntity[] = []): boolean => {
  if (left.length !== right.length) return false;
  const signatures = new Set(right.map(ruleSignature));
  return left.every((rule) => signatures.has(ruleSignature(rule)));
};
